import { useState } from "react";
import { Link } from "react-router-dom";
import { C } from "../constants/theme";
import { useSiteContent } from "../context/SiteContentContext";
import FadeIn from "../components/ui/FadeIn";
import TiltCard from "../components/ui/TiltCard";
import RainbowDivider from "../components/ui/RainbowDivider";

const WHATSAPP_NUMBER = "923390002106";

const PROGRAM_OPTIONS = [
  "Afterschool Socialisation Club",
  "Preschool (Opening January 2027)",
  "Quran for Kids",
  "Homeschool Companion",
  "Not sure yet — need guidance",
];

const Admissions = () => {
  const { content } = useSiteContent();
  const a = content.admissions || {};
  const ct = content.contact;
  const whatsappNumber = (ct?.whatsappLink || `https://wa.me/${WHATSAPP_NUMBER}`).replace(/\D/g, "").slice(-12) || WHATSAPP_NUMBER;
  const PROCESS = Array.isArray(content.admissionsProcess) ? content.admissionsProcess : [];
  const REQUIREMENTS = Array.isArray(content.admissionsReqs) ? content.admissionsReqs : [];
  const FAQS = Array.isArray(content.admissionsFaqs) ? content.admissionsFaqs : [];
  const NOT_FOR = Array.isArray(content.admissionsNotFor) ? content.admissionsNotFor : [];
  const PERFECT_FOR = Array.isArray(content.admissionsPerfectFor) ? content.admissionsPerfectFor : [];
  const [openFaq, setOpenFaq] = useState(null);

  const [fullName, setFullName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [numKids, setNumKids] = useState("");
  const [area, setArea] = useState("");
  const [program, setProgram] = useState("");
  const [source, setSource] = useState("");
  const [triedIncomplete, setTriedIncomplete] = useState(false);

  const isComplete = fullName.trim() && relationship && childName.trim() && childAge.trim() && numKids.trim() && area.trim() && program && source;

  const queryDetails = () =>
    `Full Name: ${fullName.trim()}\nRelationship to Child: ${relationship}\nChild's Name: ${childName.trim()}\nChild's Age: ${childAge.trim()}\nNumber of Kids: ${numKids.trim()}\nArea of Residence: ${area.trim()}\nInterested in: ${program}\nFound you via: ${source}`;

  const waLink = () => {
    const text = `Assalamu Alaikum! I'm interested in enrolling with Mini Muslims Nest. Here are my details:\n\n${queryDetails()}`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;
  };

  const handleSend = () => {
    if (!isComplete) { setTriedIncomplete(true); return; }
    window.open(waLink(), "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ paddingTop: 72 }}>
      {/* ── Hero ── */}
      <section data-mmn="admissions-hero" style={{ background:`linear-gradient(160deg,#8B6BE8 0%,#5c3fb5 100%)`, padding:"100px 6vw 90px", position:"relative", overflow:"hidden", minHeight:"50vh", display:"flex", alignItems:"center" }}>
        <div style={{ position:"absolute", top:-80, right:-80, width:450, height:450, borderRadius:"50%", background:"rgba(255,255,255,0.06)", pointerEvents:"none" }}/>
        <div style={{ maxWidth:1100, margin:"0 auto", position:"relative", zIndex:2 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(255,255,255,0.15)", color:C.white, fontFamily:"Nunito", fontWeight:700, fontSize:11, letterSpacing:"0.15em", padding:"6px 16px", borderRadius:100, textTransform:"uppercase", marginBottom:20 }}>Join the Nest</div>
          <h1 style={{ fontFamily:"Fredoka One", fontSize:"clamp(28px,5vw,58px)", color:C.white, lineHeight:1.1, margin:"0 0 20px", maxWidth:720 }}>
            Is This the Right<br/>School for <span style={{ color:C.yellow }}>Your Family?</span>
          </h1>
          <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:"clamp(15px,2vw,19px)", color:"rgba(255,255,255,0.85)", maxWidth:600, lineHeight:1.82, margin:0 }}>
            We do not accept every family — and we are honest about that. We accept families who are ready to walk a different road.
          </p>
        </div>
      </section>

      {/* ── Which Track ── */}
      <section data-mmn="admissions-track" style={{ background:C.warmGray, padding:"56px 6vw" }}>
        <div style={{ maxWidth:900, margin:"0 auto", background:C.white, borderRadius:20, padding:"28px 30px", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ fontSize:30, flexShrink:0 }}>🕙</div>
          <p style={{ fontFamily:"Nunito", fontSize:14, color:C.text, lineHeight:1.85, margin:0 }}>
            Everything below is our admissions process for the <b style={{ color:C.coral }}>dedicated Preschool</b> (ages 4–7), opening January 2027. Looking to join the <b style={{ color:C.mint }}>Afterschool Socialisation Club</b> instead (ages 2.5–12)? It's open now, on a rolling basis — message us on WhatsApp for the next available spot.
          </p>
        </div>
      </section>

      {/* ── Who We're For ── */}
      <section data-mmn="admissions-who" style={{ background:C.navy, padding:"100px 6vw" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:56 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.yellow, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Be Honest With Yourself</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,46px)", color:C.white, margin:"0 0 14px" }}>Is This School For You?</h2>
              <RainbowDivider />
            </div>
          </FadeIn>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:22 }}>
            {[
              { icon:"❌", color:C.coral, bg:"rgba(240,135,106,0.1)", title: a.notForHeading || "Not For You If…", points: NOT_FOR },
              { icon:"✅", color:C.mint,  bg:"rgba(75,174,149,0.1)",  title: a.perfectForHeading || "Perfectly For You If…", points: PERFECT_FOR },
            ].map(({ icon, color, bg, title, points }) => (
              <FadeIn key={title} delay={0.1}>
                <div style={{ background:bg, border:`2px solid ${color}30`, borderRadius:22, padding:"32px 28px" }}>
                  <div style={{ fontSize:36, marginBottom:14 }}>{icon}</div>
                  <div style={{ fontFamily:"Fredoka One", fontSize:20, color, marginBottom:18 }}>{title}</div>
                  {points.map(pt => (
                    <div key={pt} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:12 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:color, marginTop:7, flexShrink:0 }}/>
                      <span style={{ fontFamily:"Nunito", fontSize:14, color:"rgba(255,255,255,0.8)", lineHeight:1.65 }}>{pt}</span>
                    </div>
                  ))}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Process ── */}
      <section data-mmn="admissions-process" style={{ background:C.warmGray, padding:"100px 6vw" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:60 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Step by Step</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,46px)", color:C.navy, margin:"0 0 14px" }}>The Enrolment Process.</h2>
              <RainbowDivider />
            </div>
          </FadeIn>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:22 }}>
            {PROCESS.map(({ step, icon, color, title, body }, i) => (
              <FadeIn key={step} delay={i * 0.1}>
                <TiltCard maxTilt={4} style={{ background:C.white, borderRadius:22, padding:"28px 22px", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", border:`2px solid ${color}20` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <div style={{ fontFamily:"Fredoka One", fontSize:13, color, background:`${color}15`, padding:"4px 12px", borderRadius:100 }}>{step}</div>
                    <span style={{ fontSize:28 }}>{icon}</span>
                  </div>
                  <div style={{ fontFamily:"Fredoka One", fontSize:18, color, marginBottom:10 }}>{title}</div>
                  <p style={{ fontFamily:"Nunito", fontSize:13.5, color:C.text, lineHeight:1.75, margin:0 }}>{body}</p>
                </TiltCard>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Requirements ── */}
      <section data-mmn="admissions-req" style={{ background:C.yellowBg, padding:"80px 6vw", borderTop:`4px solid ${C.yellow}40` }}>
        <div style={{ maxWidth:780, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:44 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Before You Apply</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,40px)", color:C.navy, margin:0 }}>What We Ask of Families.</h2>
            </div>
          </FadeIn>
          {REQUIREMENTS.map((req, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div style={{ display:"flex", gap:16, alignItems:"flex-start", marginBottom:18, padding:"18px 22px", background:C.white, borderRadius:14, border:`1.5px solid ${C.yellow}40` }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:C.navy, color:C.white, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Fredoka One", fontSize:13, flexShrink:0 }}>{i+1}</div>
                <p style={{ fontFamily:"Nunito", fontSize:14.5, color:C.text, lineHeight:1.7, margin:0 }}>{req}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section data-mmn="admissions-faq" style={{ background:C.cream, padding:"100px 6vw" }}>
        <div style={{ maxWidth:780, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:52 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Frequently Asked</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,40px)", color:C.navy, margin:0 }}>Common Questions.</h2>
            </div>
          </FadeIn>
          {FAQS.map(({ q, a: ans }, i) => (
            <FadeIn key={i} delay={i * 0.05}>
              <div style={{ marginBottom:12, borderRadius:14, overflow:"hidden", border:`1.5px solid ${C.navy}14`, boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{
                  width:"100%", padding:"18px 22px", background:openFaq === i ? C.navy : C.white,
                  border:"none", cursor:"pointer", textAlign:"left",
                  display:"flex", justifyContent:"space-between", alignItems:"center", gap:16,
                  transition:"background 0.2s",
                }}>
                  <span style={{ fontFamily:"Nunito", fontWeight:700, fontSize:14.5, color:openFaq === i ? C.white : C.navy, lineHeight:1.4 }}>{q}</span>
                  <span style={{ fontSize:18, color:openFaq === i ? C.yellow : C.coral, flexShrink:0, transform:openFaq === i ? "rotate(45deg)" : "none", transition:"transform 0.2s" }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding:"18px 22px", background:"#f8f6f2", borderTop:`1px solid ${C.navy}10` }}>
                    <p style={{ fontFamily:"Nunito", fontSize:14, color:C.text, lineHeight:1.8, margin:0 }}>{ans}</p>
                  </div>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── Interest Query Form ── */}
      <section id="interest-form" data-mmn="admissions-interest-form" style={{ background:C.warmGray, padding:"100px 6vw" }}>
        <div style={{ maxWidth:640, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:40 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Ready to Take the Next Step?</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,38px)", color:C.navy, margin:"0 0 14px" }}>Interested to Enroll?</h2>
              <RainbowDivider />
            </div>
          </FadeIn>
          <FadeIn delay={0.06}>
            <div style={{ background:C.white, borderRadius:24, padding:"40px 36px", boxShadow:"0 8px 32px rgba(0,0,0,0.07)" }}>
              <h3 style={{ fontFamily:"Fredoka One", fontSize:19, color:C.navy, margin:"0 0 8px", textAlign:"center" }}>Submit Your Interest Query Form Now</h3>
              <p style={{ fontFamily:"Nunito", fontSize:13.5, color:C.muted, lineHeight:1.7, margin:"0 0 26px", textAlign:"center" }}>
                Fill in your details and send — it opens WhatsApp with your message pre-filled so nothing gets lost.
              </p>

              <input
                type="text" placeholder="Full Name *" value={fullName} onChange={e=>setFullName(e.target.value)}
                style={{ width:"100%", padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12, fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none", boxSizing:"border-box", marginBottom:12 }}
              />

              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, marginBottom:8 }}>Relationship to Child *</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["Mother","Father","Guardian","Other"].map(val => (
                    <button key={val} type="button" onClick={()=>setRelationship(val)} style={{
                      background: relationship === val ? C.navy : C.white,
                      color: relationship === val ? C.white : C.navy,
                      border:`2px solid ${C.navy}`, fontFamily:"Nunito", fontWeight:700, fontSize:12.5,
                      padding:"7px 14px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                    }}>{val}</button>
                  ))}
                </div>
              </div>

              <div style={{ display:"flex", gap:12, marginBottom:12 }}>
                <input
                  type="text" placeholder="Name of Child *" value={childName} onChange={e=>setChildName(e.target.value)}
                  style={{ flex:2, minWidth:0, padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12, fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none", boxSizing:"border-box" }}
                />
                <input
                  type="text" placeholder="Age of Child *" value={childAge} onChange={e=>setChildAge(e.target.value)}
                  style={{ flex:1, minWidth:0, padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12, fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none", boxSizing:"border-box" }}
                />
              </div>

              <div style={{ display:"flex", gap:12, marginBottom:16 }}>
                <input
                  type="text" placeholder="Number of Kids *" value={numKids} onChange={e=>setNumKids(e.target.value)}
                  style={{ flex:1, minWidth:0, padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12, fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none", boxSizing:"border-box" }}
                />
                <input
                  type="text" placeholder="Area of Residence *" value={area} onChange={e=>setArea(e.target.value)}
                  style={{ flex:2, minWidth:0, padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12, fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none", boxSizing:"border-box" }}
                />
              </div>

              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, marginBottom:8 }}>Interested in Which Programme? *</div>
                <select value={program} onChange={e=>setProgram(e.target.value)} style={{
                  width:"100%", padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12,
                  fontFamily:"Nunito", fontSize:14, color: program ? C.text : C.muted, background:C.white, outline:"none", boxSizing:"border-box",
                }}>
                  <option value="" disabled>Select a programme…</option>
                  {PROGRAM_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              <div style={{ marginBottom:24 }}>
                <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, marginBottom:8 }}>Where Did You Hear About Us? *</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["Facebook","Instagram","Google","Other"].map(val => (
                    <button key={val} type="button" onClick={()=>setSource(val)} style={{
                      background: source === val ? C.navy : C.white,
                      color: source === val ? C.white : C.navy,
                      border:`2px solid ${C.navy}`, fontFamily:"Nunito", fontWeight:700, fontSize:12.5,
                      padding:"7px 14px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                    }}>{val}</button>
                  ))}
                </div>
              </div>

              <button type="button" onClick={handleSend} style={{
                display:"block", width:"100%", background: isComplete ? "#25D366" : `${C.navy}30`,
                color: isComplete ? C.white : C.navy, border:"none",
                fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"15px", borderRadius:100, cursor:"pointer",
              }}>💬 Submit Interest via WhatsApp</button>
              {triedIncomplete && !isComplete && (
                <p style={{ fontFamily:"Nunito", fontSize:12, color:C.coral, marginTop:10, textAlign:"center" }}>
                  Please fill in every field above so we can follow up properly.
                </p>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background:C.navy, padding:"80px 6vw" }}>
        <div style={{ maxWidth:640, margin:"0 auto", textAlign:"center" }}>
          <FadeIn>
            <div style={{ fontSize:40, marginBottom:20 }}>🕌</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,40px)", color:C.white, margin:"0 0 14px" }}>Bring Your Courage. Your Trust. Your Intent.</h2>
            <p style={{ fontFamily:"Nunito", fontSize:15, color:"rgba(255,255,255,0.7)", lineHeight:1.8, marginBottom:36 }}>Start with a visit. No paperwork. No pressure. Just a conversation between two families.</p>
            <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
              <Link to="/contact" style={{ background:C.yellow, color:C.navy, textDecoration:"none", fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"16px 36px", borderRadius:100, display:"inline-block" }}>Book a Visit</Link>
              <a href="https://wa.me/923390002106" style={{ background:"#25D366", color:C.white, textDecoration:"none", fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"16px 36px", borderRadius:100, display:"inline-block" }}>💬 WhatsApp</a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
